import { useState } from 'react';
import Card from '../../../components/Card.jsx';
import Button from '../../../components/Button.jsx';
import Chip from '../../../components/Chip.jsx';
import DataTable from '../../../components/DataTable.jsx';
import { backtestApi } from '../../../lib/apiClient.js';
import ConfigPanel from '../ConfigPanel.jsx';
import StrategyPreview from '../StrategyPreview.jsx';
import ErrorNote from '../ErrorNote.jsx';
import EquityCurveChart from '../EquityCurveChart.jsx';
import useRunResult from '../../../utils/useRunResult.js';
import { engineConfig } from '../../../utils/engineConfig.js';
import { number, percent } from '../../../utils/format.js';
import { isBacktestPeriodValid } from '../../../utils/backtestPeriod.js';

// Demo multi-strategy roster. A live version would list the user's saved
// strategies (from Rules/Signals/Optimize runs); kept as a static preset
// list here so this mode fully renders offline.
const STRATEGY_PRESETS = [
  { id: 's_ema', label: 'EMA Crossover — Pan-African', strategy: { side: 'long', entry: { all: [{ left: 'close', op: 'cross_above', right: { indicator: 'ema', period: 20 } }] }, exit: { all: [{ left: 'close', op: 'cross_below', right: { indicator: 'ema', period: 20 } }] } } },
  { id: 's_rsi', label: 'RSI Mean-Reversion — NGX/NSE', strategy: { side: 'long', entry: { all: [{ left: { indicator: 'rsi', period: 14 }, op: '<', right: 30 }] }, exit: { all: [{ left: { indicator: 'rsi', period: 14 }, op: '>', right: 60 }] } } },
  { id: 's_brk', label: 'Breakout Momentum — JSE', strategy: { side: 'long', entry: { all: [{ left: 'close', op: '>', right: { indicator: 'sma', period: 50 } }] }, exit: { all: [{ left: 'close', op: '<', right: { indicator: 'sma', period: 20 } }] } } },
  { id: 's_macd', label: 'MACD Trend — EGX/GSE', strategy: { side: 'long', entry: { all: [{ left: { feature: 'macd' }, op: '>', right: 0 }] }, exit: { all: [{ left: { feature: 'macd' }, op: '<', right: 0 }] } } },
];

const CONTRIBUTION_COLUMNS = [
  { key: 'label', header: 'Strategy' },
  { key: 'weight', header: 'Weight', numeric: true, render: (r) => `${(r.weight * 100).toFixed(1)}%` },
  {
    key: 'strategyReturn',
    header: 'Return',
    numeric: true,
    render: (r) => <span className={r.strategyReturn >= 0 ? 'aq-num-gain' : 'aq-num-loss'}>{percent(r.strategyReturn)}</span>,
  },
  {
    key: 'contributionPct',
    header: 'Contribution',
    numeric: true,
    render: (r) => <span>{percent(r.contributionPct)}</span>,
  },
  { key: 'sharpe', header: 'Sharpe', numeric: true, render: (r) => number(r.sharpe) },
];

/**
 * Portfolio mode: multi-strategy correlation + diversification, per
 * `/v1/portfolio` wrapping `{strategies[], allocation}`
 * (OBT_INTEGRATION_SPEC.md §1/§2). Distinct from the "Portfolio &
 * Backtest" holdings-construction page — this is strategy-level, not
 * symbol-weight-level.
 */
export default function PortfolioMode({ config, onConfigChange, wallet, onRan, lastRun, onSaveRun }) {
  const [selected, setSelected] = useState(['s_ema', 's_rsi']);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useRunResult(lastRun?.result, true);
  const [error, setError] = useState(null);

  function toggle(id) {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  const selectedPresets = STRATEGY_PRESETS.filter((s) => selected.includes(s.id));
  const strategies = selectedPresets.map((preset) => ({
    mode: 'rules', strategy: preset.strategy, symbols: config.symbols, period: config.period,
    config: engineConfig(config),
  }));
  const equalWeight = strategies.length ? 1 / strategies.length : 0;
  const names = selectedPresets.map((preset) => preset.label);
  const weights = Object.fromEntries(names.map((name) => [name, equalWeight]));

  const request = {
    strategies,
    names,
    allocation: 'explicit',
    weights,
    capital: config.capital,
  };

  async function run() {
    setError(null);
    setRunning(true);
    try {
      const data = await backtestApi.portfolio(request);
      setResult(data);
      onSaveRun?.(data);
      onRan?.();
    } catch (err) {
      setError(err);
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="aq-grid aq-grid--2">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <ConfigPanel config={config} onChange={onConfigChange} wallet={wallet} />

        <Card>
          <h3 className="heading" style={{ fontSize: 16, marginBottom: 12 }}>
            Strategies in this portfolio
          </h3>
          {STRATEGY_PRESETS.map((s) => (
            <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 8 }}>
              <input type="checkbox" checked={selected.includes(s.id)} onChange={() => toggle(s.id)} />
              {s.label}
            </label>
          ))}
          <p className="aq-module-desc">Equal-weighted across the {strategies.length} selected strategies for this demo.</p>

          <StrategyPreview title="portfolio request preview" value={request} />

          <div style={{ marginTop: 14 }}>
            <Button disabled={running || strategies.length < 2 || !config.symbols.length || !(Number(config.capital) > 0) || !isBacktestPeriodValid(config.period)} onClick={run}>
              {running ? 'Running…' : 'Run portfolio analysis'}
            </Button>
          </div>
          {strategies.length < 2 && <p className="aq-module-desc">Select at least 2 strategies to analyze diversification.</p>}
          {!config.symbols.length && <p className="aq-note">Select at least one symbol before running portfolio analysis.</p>}
        </Card>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Card>
          <h3 className="heading" style={{ fontSize: 16, marginBottom: 12 }}>
            Combined equity
          </h3>
          <ErrorNote error={error} />
          {!result && !error && <p className="aq-module-desc">Run the analysis to see combined equity + correlation.</p>}
          {result && <EquityCurveChart equity={result.equity || []} label="Combined portfolio" />}
          {(result?.assumptions || []).map((assumption) => <p className="aq-note" key={assumption}>{assumption}</p>)}
        </Card>

        {result && (
          <>
            {Array.isArray(result.correlation) && <Card>
              <h3 className="heading" style={{ fontSize: 16, marginBottom: 12 }}>
                Correlation matrix
              </h3>
              <div
                className="aq-heat"
                style={{ gridTemplateColumns: `140px repeat(${result.correlation.length}, 1fr)` }}
              >
                <div />
                {result.correlation.map((row) => (
                  <div key={`h-${row.id}`} className="aq-heat-label">
                    {row.label}
                  </div>
                ))}
                {result.correlation.map((row) => (
                  <div key={`row-${row.id}`} style={{ display: 'contents' }}>
                    <div className="aq-heat-label">{row.label}</div>
                    {row.values.map((v, j) => {
                      const bg = v >= 0 ? `rgba(5,126,255,${Math.abs(v)})` : `rgba(206,42,42,${Math.abs(v)})`;
                      return (
                        <div key={j} className="aq-heat-cell num" style={{ background: bg }}>
                          {number(v)}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </Card>}

            {Array.isArray(result.contributions) && <Card>
              <h3 className="heading" style={{ fontSize: 16, marginBottom: 12 }}>
                Per-strategy contribution
              </h3>
              <DataTable columns={CONTRIBUTION_COLUMNS} rows={result.contributions} />
              <div className="aq-metric-row" style={{ marginTop: 10 }}>
                <span>
                  Charged <strong className="num">${result.credit?.charged?.toFixed?.(4)}</strong>
                </span>
                <Chip tone="info">{result.meta?.engine} · portfolio</Chip>
              </div>
            </Card>}
            {result.error && <Card><p className="aq-auth-alert aq-auth-alert--error">{result.error}</p></Card>}
          </>
        )}
      </div>
    </div>
  );
}
